<template>
  <div class="hotspot critical-component-clear">
    <header
      class="
        hotspot__header
        container
        text-center
      "
    >
      <p
        v-if="subtitle"
        class="
          hotspot__subtitle
          text-other-label-s
          text-other-label-l-desktop
        "
        v-text="subtitle"
      />

      <h2
        v-if="title"
        class="
          hotspot__title
          text-heading-xl
          text-heading-3xl-desktop
          text-center
        "
        v-text="title"
      />

      <btn
        v-if="cta.text && cta.url"
        class="hotspot__cta"
        :label="cta.text"
        modifiers="text"
        :url="cta.url"
      />
    </header>

    <div
      ref="media"
      v-click-outside="handleClickOutside"
      class="hotspot__media"
    >
      <template
        v-for="(poi) of formattedPoi"
        :key="`${liquid.sectionId}-${poi.id}`"
      >
        <component
          :is="poi.enableModal ? 'button' : 'a'"
          :ref="(args) => setItemRefs(args, poi.id, 'poi')"
          class="hotspot__poi hotspot__dot"
          :class="{
            [$class('active')]: activePoiId === poi.id,
            [$class('visible')]: !visiblePoi || visiblePoi && visiblePoi.includes(poi.id),
          }"
          :href="poi.enableModal ? null : poi.url"
          :style="poi.styles"
          :type="poi.enableModal ? 'button' : null"
          @click="handlePoiClick(poi)"
        >
          <icon-plus />

          <span
            class="hotspot__poi-text visually-hidden"
            v-text="getPoiText(poi)"
          />
        </component>

        <hotspot-modal
          v-if="poi.enableModal"
          :ref="(args) => setItemRefs(args, poi.id, 'modal')"
          class="hotspot__modal hotspot__modal--desktop"
          :active-poi-id="activePoiId"
          :poi="poi"
          @update-active-poi-id="(payload) => activePoiId = payload"
        />
      </template>

      <responsive-image
        v-if="image && !hasVideo"
        class="hotspot__image-container"
        :image="image"
        :loaded-animation="false"
        :ratio="liquid.ratio"
        sizes="100vw"
        @click="handleImageClick"
      />

      <hotspot-video
        v-if="hasVideo"
        ref="hotspotVideo"
        class="hotspot__video"
        :active-poi-id="activePoiId"
        :formatted-poi="formattedPoi"
        :image="image ? image : null"
        :subtitles="videoSubtitles"
        :video="video"
        @update-active-poi-id="(payload) => activePoiId = payload"
        @update-visible-poi="(payload) => visiblePoi = payload"
      />
    </div>

    <template
      v-for="(poi) of formattedPoi"
      :key="`${liquid.sectionId}-${poi.id}`"
    >
      <hotspot-modal
        v-if="poi.enableModal"
        :ref="(args) => setItemRefs(args, poi.id, 'modal')"
        class="hotspot__modal hotspot__modal--mobile"
        :active-poi-id="activePoiId"
        :poi="poi"
        @update-active-poi-id="(payload) => activePoiId = payload"
      />
    </template>
  </div>
</template>

<script>

/**
 * Vue: Hotspot (hotspot)
 * -----------------------------------------------------------------------------
 * Display an image or video with highlighted points of interest to show extra
 * information.
 *
 * @param {Object} [cta] - Section CTA.
 * @param {Boolean|Object} [image] - Shopify image object.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Array} points - Section blocks for each point of interest.
 * @param {String} [subtitle] - Section subtitle.
 * @param {String} [title] - Section title.
 * @param {Object} [video] - Formatted media video object.
 * @param {String} [videoSubtitles] - Video subtitles URL.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import HotspotModal from '~async/hotspot-section/hotspot-modal'
import HotspotVideo from '~async/hotspot-section/hotspot-video'
import ResponsiveImage from '~global/images/responsive-image'

import IconClose from '~icons/directional-navigation/close.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'

import timings from '~/config/timings'

export default {
  name: 'HotspotSection',

  components: {
    Btn,
    IconClose,
    IconPlus,
    HotspotModal,
    HotspotVideo,
    ResponsiveImage,
  },

  props: {
    cta: {
      type: Object,
      default: () => ({
        text: '',
        url: '',
      }),
    },
    image: {
      type: [Boolean, Object],
      default: () => ({
        alt: '',
        height: 0,
        src: '',
        width: 0,
      }),
    },
    liquid: {
      type: Object,
      default: () => ({
        ratio: '16:9',
        sectionId: '',
      }),
    },
    points: {
      type: Array,
      default: () => [],
    },
    subtitle: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    video: {
      type: Object,
      default: () => ({
        format: 'mp4',
        mimeType: 'video/mp4',
        url: '',
      }),
    },
    videoSubtitles: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      activePoiId: '',
      formattedPoi: [],
      itemRefs: [],
      visiblePoi: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      previousElement: (state) => state.accessibility.previousElement,
      screenWidth: (state) => state.index.screen.width,
    }),

    /**
     * Compute if section has video.
     * @returns {Boolean}
     */
    hasVideo() {
      return this.video && this.video.sources
    },
  },

  watch: {

    /**
     * Watch active POI to update focus on change.
     */
    activePoiId() {
      if (this.activePoiId) {
        this.openModal()
        return
      }

      this.closeModal()
    },

    /**
     * Watch mobile state to update trap focus.
     */
    isMobile() {
      if (!this.activePoiId) {
        return
      }

      this.openModal(true)
    },

    /**
     * Watch screen width to update POI positions.
     */
    screenWidth() {
      this.getFormattedPoi()
    },

    /**
     * Watch visible POI to close modal if it's no longer visible.
     */
    visiblePoi() {
      if (!this.visiblePoi) {
        return
      }

      if (this.visiblePoi.includes(this.activePoiId)) {
        return
      }

      this.activePoiId = ''
    },
  },

  mounted() {
    this.getFormattedPoi()

    /**
     * Close any active POI on esc keypress.
     */
    cnvs.EventBus.listen('cnvs:esc', () => {
      this.activePoiId = ''
    })
  },

  beforeUpdate() {
    this.itemRefs = []
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPreviousElement: 'accessibility/setPreviousElement',
    }),

    /**
     * Get POI text based on if it's active or not.
     * @param {Object} poi - Current point of interest.
     * @returns {String}
     */
    getPoiText(poi) {
      if (poi.id === this.activePoiId) {
        return this.$string('accessibility.close')
      }

      return `${this.$string('accessibility.read_more')} ${poi.content.title}`
    },

    /**
     * Set item refs.
     * @param {HTMLElement} element - Item in v-for.
     * @param {String} id - ID of current point of interest.
     * @param {String} type - Type of item, 'modal' or 'poi'.
     */
    setItemRefs(item, id, type) {
      if (!item) {
        return
      }

      const element = item.$el ? item.$el : item
      this.itemRefs.push({ element, id, type })
    },

    /**
     * Close modal.
     */
    closeModal() {
      this.clearFocusTrap()

      if (this.previousElement) {
        this.previousElement.focus()
      }

      this.setPreviousElement()
    },

    /**
     * Open modal.
     * @param {Boolean} stateChange - Triggered on mobile state change?
     */
    async openModal(stateChange) {
      if (!stateChange) {
        this.setPreviousElement(document.activeElement)
      }

      await this.$nextTick()
      const modal = this.itemRefs.find((item) => {
        return item.id === this.activePoiId && item.type === 'modal'
      })

      const poi = this.itemRefs.find((item) => {
        return item.id === this.activePoiId && item.type === 'poi'
      })

      /**
       * If desktop then include POI in trap focus to act as close button.
       */
      this.setFocusTrap({
        container: modal.element,
        elements: this.isMobile ? [] : [poi.element],
        prepend: true,
      })

      await cnvs.Sleep(timings.normal)

      if (!this.getFirstElement) {
        return
      }

      this.getFirstElement.focus({ preventScroll: true })
    },

    /**
     * Get formatted POI.
     * - Filter out POI with no URL.
     * - Builds POI styles to prevent them falling outside the media.
     * - Updates order so tab order is logical.
     * - If vertical position is the same then use horizontal position.
     * @returns {Array}
     */
    getFormattedPoi() {
      const clonedPoi = [...this.points]

      let formattedPoi = clonedPoi
        .filter((poi) => poi.url)
        .map((poi) => this.getPoiStyles(poi))
        .sort((a, b) => {
          if (a.position.y === b.position.y) {
            return a.position.x - b.position.x
          }

          return a.position.y - b.position.y
        })

      /**
       * If hotspot video component exists then update video timings.
       */
      if (this.$refs.hotspotVideo) {
        formattedPoi = formattedPoi.map((poi) => {
          return this.$refs.hotspotVideo.getPoiTiming(poi)
        })
      }

      this.formattedPoi = formattedPoi
    },

    /**
     * Get POI styles based on position.
     * - Prevents them falling outside the media.
     * @param {Object} poi - Current point of interest.
     * @returns {Object}
     */
    getPoiStyles(poi) {
      const mediaHeight = this.$refs.media.offsetHeight
      const mediaWidth = this.$refs.media.offsetWidth

      const poiSize = getComputedStyle(document.documentElement)
        .getPropertyValue('--hotspot-poi-size')
        .replace('px', '')

      const poiPixelPositionX = mediaWidth * (poi.position.x / 100)
      const poiPixelPositionY = mediaHeight * (poi.position.y / 100)

      /**
       * Calculate remaining space between POI and edge of media.
       */
      const remainingSpace = {
        bottom: (mediaHeight - poiPixelPositionY) - (poiSize / 2),
        left: poiPixelPositionX - (poiSize / 2),
        right: (mediaWidth - poiPixelPositionX) - (poiSize / 2),
        top: poiPixelPositionY - (poiSize / 2),
      }

      /**
       * Determine offset based on negative remaining space.
       */
      let xAxis = 0
      let yAxis = 0

      if (remainingSpace.left < 0) {
        xAxis = Math.abs(remainingSpace.left)
      } else if (remainingSpace.right < 0) {
        xAxis = remainingSpace.right
      }

      if (remainingSpace.top < 0) {
        yAxis = Math.abs(remainingSpace.top)
      } else if (remainingSpace.bottom < 0) {
        yAxis = remainingSpace.bottom
      }

      return {
        ...poi,
        offset: {
          x: xAxis,
          y: yAxis,
        },
        styles: {
          left: `calc(${poi.position.x}% - (var(--hotspot-poi-size) / 2) + ${xAxis}px)`,
          top: `calc(${poi.position.y}% - (var(--hotspot-poi-size) / 2) + ${yAxis}px)`,
        },
      }
    },

    /**
     * Handle click outside of section.
     */
    handleClickOutside() {
      if (!this.activePoiId || this.isMobile) {
        return
      }

      this.activePoiId = ''
    },

    /**
     * Handle image click.
     * - Means they haven't clicked modal or POI so close if active POI ID.
     */
    handleImageClick() {
      this.handleClickOutside()
    },

    /**
     * Handle clicking point of interest.
     * - Only one POI can be active at a time.
     * @param {Object} poi - Clicked POI.
     */
    handlePoiClick(poi) {
      if (this.activePoiId) {
        const previousPoiId = this.activePoiId
        this.activePoiId = ''

        if (previousPoiId === poi.id) {
          return
        }
      }

      if (!poi.enableModal) {
        return
      }

      this.activePoiId = poi.id
    },
  },
}
</script>

<style lang="scss">
@import './hotspot-section';
</style>
